import { z } from "zod/v3";

// Common validation schemas
export const NullableStringSchema = z.string().transform((v: string) => (v === "" ? null : v));

export const NullableNumberSchema = z.union([z.number().int().positive().min(1), z.nan()]).nullable();

export const URLSchema = z
  .string()
  .url("Please enter a valid website URL starting with http:// or https://")
  .or(z.literal(""))
  .transform((v: string) => (v === "" ? null : v));

// Address validation — mirror person ContactDetails behavior: US requires state + postal code;
// international addresses may omit postal code (or use non‑US formats) and use province/region in `state`.
const US_ZIP_PATTERN = /^\d{5}(-\d{4})?$/;

export const AddressSchema = z
  .object({
    address1: z.string().min(1, "Address 1 is required"),
    address2: z.string().optional(),
    city: z.string().min(1, "City is required"),
    countryCode: z.string().length(3, "Country is required"),
    postalCode: z.string(),
    state: z.string(),
    addressType: z.string()
  })
  .superRefine((data, ctx) => {
    if (data.countryCode !== "USA") return;

    if (!data.state.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "State is required", path: ["state"] });
    }
    const postal = data.postalCode.trim();
    if (!postal) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Postal Code is required", path: ["postalCode"] });
    } else if (!US_ZIP_PATTERN.test(postal)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Enter a valid postal code", path: ["postalCode"] });
    }
  });

export const EmptyAddressSchema = z.object({
  address1: z.literal(""),
  address2: z.literal("").optional(),
  city: z.literal(""),
  countryCode: z.literal(""),
  postalCode: z.literal(""),
  state: z.literal(""),
  addressType: z.literal("")
});

export const OptionalAddressSchema = z.union([EmptyAddressSchema, AddressSchema]).transform((addr) => {
  const isEmptyAddress = Object.values(addr).every((v) => v === "");
  return isEmptyAddress ? null : addr;
});

// Yes/No/Null option utilities and schema
export function yesNoNullOptionToNullableBoolean(v: unknown): boolean | null {
  if (typeof v === "boolean") return v;
  if (v === "" || v == null) return null;
  return v === "Yes";
}

export function nullableBooleanToYesNoNullOption(value: boolean | null): "Yes" | "No" | "" {
  return value === true ? "Yes" : value === false ? "No" : "";
}

export const YesNoNullOptionsSchema = z.preprocess(yesNoNullOptionToNullableBoolean, z.boolean().nullable());
